import React, { useEffect, useRef, useState } from "react";
import "./Home.css";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Lottie from "lottie-react";
import * as homePageAnimation from "../../assets/huddle-meeting-animation.json";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue, } from "recoil";
import { isMeeetingDisconnected, participantDetailState, showForceUpdateDialogState, staticConfigAtom } from "../../atom/atom";
import ForceUpdateDialog from "../force-update-dialog/ForceUpdateDialog";
import { IJoinMeeting } from "../../models/IJoinMeeting";
import JoinMeetingService from "../../services/joinMeeting.service";
import Loading from "../../utils/Loading";
import { StaticConfig } from "../../models/IStaticConfig";
import { isForceUpdateRequired } from "../../services/updateChecker.service";
import { clearSessionDetails, getDataFromLocalStorage } from "../../services/storage.service";
import { getStaticConfig } from "../../services/config.service";
import { TokenPayload } from "../../models/IToken";
import TokenService from "../../services/token.service";
import useSnackbar from "../../utils/useSnackbar";

const Home: React.FC = () => {

  const hasInitialized = useRef(false);
  const navigate = useNavigate();
  const [showForceUpdateDialog, setShowForceUpdateDialog] = useRecoilState(showForceUpdateDialogState);
  const [staticConfig, setStaticConfig] = useRecoilState(staticConfigAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [isMeetingDisconneted, setIsMeetingDisconnected] = useRecoilState(isMeeetingDisconnected);
  const [ParticipantDetails, setParitipantDetails] = useRecoilState(participantDetailState);
  const [fieldDisabled, setFieldDisabled] = useState(false);
  const [joinBtnLabel, setJoinBtnLabel] = useState("Join");

  const handleSnackbarNoticationEvent = (message: string) => {
    showSnackbar({
      message: message,
      severity: "warning",
    });
  };


  const [formValues, setFormValues] = useState({ name: "", email: "", meetingId: "" });
  const [errors, setErrors] = useState({ name: "", email: "", meetingId: "", });

  const getStaticConfigAndCheckForceUpdate = async () => {

    setIsLoading(true);

    await getStaticConfig();
    const fetchedConfig = getDataFromLocalStorage('staticConfig');
    setStaticConfig(fetchedConfig);

    await CheckForceUpdate(fetchedConfig);
    setIsLoading(false);

  }

  const CheckForceUpdate = async (fetchedConfig: StaticConfig) => {
    const forceUpdateRequired = await isForceUpdateRequired(fetchedConfig);
    if (forceUpdateRequired) {
      setShowForceUpdateDialog(true);
      setIsDisable(true);
      navigate("/");
      clearSessionDetails();
    }
  };

  const getSessionId = () => {
    let parsedToken: TokenPayload = TokenService.parseToken(true, "");
    return parsedToken.video.room;
  };

  const updateTitleForAgent = () => {
    if(!!(window as any).electronAPI){
      const sessionId = getSessionId();
      (window as any).electronAPI.updateTitle(`- ${sessionId}`);
    }
  }

  const handleDialogClose = () => {
    setShowForceUpdateDialog(false);
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };


  const validateMeetingId = (meetingId: string) => {
    const meetingDetails = meetingId.split("-").join("");
    const formattedMeetingId = meetingDetails.match(/.{1,3}/g)?.join("-");
    return formattedMeetingId === meetingId;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let formattedValue = value;

    if (name === "meetingId") {
      formattedValue = formatMeetingId(value);
    }

    setFormValues({
      ...formValues,
      [name]: formattedValue,
    });

    setErrors({
      ...errors,
      [name]: "",
    });
  };


  const formatMeetingId = (value: string) => {

    const numericValue = value.replace(/\D/g, "").slice(0, 9);

    return (
      numericValue
        .match(/(\d{1,3})(\d{1,3})?(\d{1,3})?/)
        ?.slice(1, 4)
        .filter(Boolean)
        .join("-") || ""
    );
  };

  useEffect(() => {
    if(isMeetingDisconneted && ParticipantDetails){

      console.log('this is somehitng', isMeetingDisconneted)
        console.log('participant details', ParticipantDetails)
      setFormValues({
        name: '' ,
        email: '',
        meetingId: ParticipantDetails.sessionId
      });

      console.log('this is form values', formValues);
      setFieldDisabled(true);
      setJoinBtnLabel("Rejoin");
    }
  },[isMeetingDisconneted , ParticipantDetails])

  useEffect(() => {
    const initAsync = async () => {
      if (!hasInitialized.current) {
        hasInitialized.current = true; 
        await getStaticConfigAndCheckForceUpdate();

        const sessionDetails = sessionStorage.getItem("session-details");
        if (sessionDetails) {
          try {
            const { sessionToken } = JSON.parse(sessionDetails);

            localStorage.setItem("token", sessionToken);

            const sessionId = getSessionId();
            (window as any).electronAPI.updateTitle(sessionId);

            updateTitleForAgent();
            navigate("/session");

          } catch (error) {
            console.error("Error parsing session details:", error);
          }
        }
      }
    };

    initAsync();
  }, [navigate]);

  const transformJoinParticipantApiBody = async (meetingId: string, name: string, email: string): Promise<IJoinMeeting> => {
    return {
      pid: meetingId,
      userName: name,
      userEmail: email
    };
  }

  const handleSubmit = async () => {
    const newErrors = {
      name: formValues.name ? "" : "Name is required",
      email: validateEmail(formValues.email) ? "" : "Invalid email format",
      meetingId: validateMeetingId(formValues.meetingId)
        ? ""
        : "Meeting ID must be in xxx-xxx-xxx format",
    };

    setErrors(newErrors);

    const isValid = !Object.values(newErrors).some((error) => error);

    if (isValid) {

      const payload = await transformJoinParticipantApiBody(formValues.meetingId, formValues.name, formValues.email);
      try {
        const res = await JoinMeetingService.createTokenForNewParticipant(payload);
      if (res && res.token) {
        localStorage.setItem("token", res.token);

        updateTitleForAgent();
        navigate("/session");
      }
      } catch (error) {
        handleSnackbarNoticationEvent('Something went wrong, please contact administrator')
      }
    }
  };

  const handleNewSession = () => {
    setFieldDisabled(false);
    setFormValues({ name: "", email: "", meetingId: "" });
    setIsMeetingDisconnected(false);
    setParitipantDetails(null);
    setJoinBtnLabel("Join");
    setIsDisable(false);
    clearSessionDetails();
  }

  if (!staticConfig) {
    return <Loading open={isLoading} />
  }
  return (
    <>
    {SnackbarComponent}
      <div className="layout-container">
        <div className="animation-section">
          <div className="animation-content">
            <Lottie animationData={homePageAnimation} loop={true} />
            <h1 className="heading-animation">
              We re-engineered the service we built for secure virtual meetings.
            </h1>
          </div>
        </div>
        <div className="form-section">
          <div className="form-container">
            <h1>Welcome to Huddle: Secure and Efficient Virtual Meetings</h1>
            <Stack direction="column" spacing={3}>
              <TextField
                fullWidth
                id="meetingId"
                name="meetingId"
                label="Enter meeting code"
                variant="outlined"
                value={formValues.meetingId}
                onChange={handleInputChange}
                error={!!errors.meetingId}
                helperText={errors.meetingId}
                disabled={fieldDisabled}
              />
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Enter your name"
                variant="outlined"
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
              />
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Enter your email"
                variant="outlined"
                onChange={handleInputChange}
                error={!!errors.email}
                helperText={errors.email}
              />
              <span>
                <Button
                  className="joinBtn"
                  style={{
                    height: "100%",
                    marginTop: "20px",
                  }}
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={isDisable}
                >
                  {joinBtnLabel}
                </Button>
              </span>
              
              {fieldDisabled && (
 <span>
                <Button
                  className="joinBtn"
                  style={{
                    height: "100%",
                    marginTop: "20px",
                  }}
                  onClick={handleNewSession}
                  variant="outlined"
                >
                  Join new session
                </Button>
              </span>
              )}
              


            </Stack>
          </div>
        </div>
      </div>


      <ForceUpdateDialog isOpen={showForceUpdateDialog} onClose={handleDialogClose} />
    </>
  );
};

export default Home;
